import {
  DetailedHTMLProps,
  ElementRef,
  LiHTMLAttributes,
  ReactNode,
  forwardRef,
  useState,
} from 'react';

import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  type Placement,
} from '@floating-ui/react';
import { Menu, Transition } from '@headlessui/react';
import { FaCaretDown } from 'react-icons/fa';
import { cn } from 'utils/helpers';

export interface NavOption
  extends DetailedHTMLProps<LiHTMLAttributes<HTMLLIElement>, HTMLLIElement> {
  key: string | number;
  isSelected?: boolean;
  text: string;
  description?: string;
  disabled?: boolean;
  ['data-tut']?: string;
}

interface PROPS {
  className?: string;
  label?: ReactNode;
  options?: NavOption[];
  optionsClassName?: string;
  disabled?: boolean;
  onClick?: (param?: any) => void;
  showArrow?: boolean;
  placement?: Placement;
}

const NavAction = forwardRef<ElementRef<'button'>, PROPS>(
  (
    {
      className,
      label,
      options,
      onClick,
      optionsClassName,
      placement = 'bottom-end',
      showArrow,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);

    const { refs, floatingStyles } = useFloating({
      placement,
      strategy: 'fixed',
      middleware: [offset({ mainAxis: 2 }), flip()],
      whileElementsMounted: autoUpdate,
    });

    return (
      <Menu as="div" ref={refs.setReference} className={cn(className)}>
        <Menu.Button
          ref={ref}
          disabled={!options?.length}
          className={cn(
            'group relative font-semibold text-secondary-base',
            'hover:text-primary-base transition text-secondary-dark',
            'disabled:bg-transparent disabled:text-disabled',
            'flex items-center gap-x-2 min-h-[20px]',
          )}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
          }}
          {...props}
        >
          {label || null}
          {showArrow && <FaCaretDown className="w-4 h-4" />}
        </Menu.Button>

        {!!options?.length && (
          <Transition
            className={'z-[100] relative'}
            as={'div'}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              ref={refs.setFloating}
              style={floatingStyles}
              className={cn(
                'z-[100] bg-fill-tertiary shadow rounded-md px-5 py-2 min-w-[140px] w-auto',
                'overflow-y-auto',
                optionsClassName,
              )}
            >
              {options?.map((opt) => (
                <Menu.Item key={opt.key} disabled={opt.disabled}>
                  {({ active, disabled }) => (
                    <li
                      data-tut={opt['data-tut']}
                      className={cn(
                        'w-full',
                        'py-2 text-xs leading-5 text-secondary-dark',
                        'flex flex-col truncate',
                        disabled && 'opacity-50 cursor-not-allowed',
                        opt.className,
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        !disabled && (onClick?.(opt) ?? opt.onClick?.(e));
                      }}
                    >
                      <span
                        className={cn(
                          'cursor-pointer truncate text-left',
                          opt.isSelected ||
                            (active && 'font-semibold text-primary-base'),
                        )}
                      >
                        {opt.text}
                      </span>
                      {opt.description && (
                        <span className="text-xs text-gray-500">
                          {opt.description}
                        </span>
                      )}
                    </li>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        )}
      </Menu>
    );
  },
);

NavAction.displayName = 'NavAction';
export default NavAction;
